import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button, Spin, message, Space, Typography, Divider } from 'antd';
import { MailOutlined, ReloadOutlined, CheckCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../AuthProvider';
import { sendEmailVerification } from 'firebase/auth';
import '../pages/auth/auth.css';

const { Title, Text, Paragraph } = Typography;

const EmailVerificationMiddleware = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const location = useLocation();
  
  // Check if user just signed up (coming from signup page)
  const isNewUser = location.state?.fromSignup || false;

  useEffect(() => {
    if (currentUser) {
      // Reload user to get the latest emailVerified status
      currentUser.reload().then(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const handleResendVerification = async () => {
    try {
      setSendingEmail(true);
      await sendEmailVerification(currentUser);
      message.success({
        content: 'Verification email sent successfully! Please check your inbox.',
        duration: 4,
      });
    } catch (error) {
      console.error('Error sending verification email:', error);
      message.error('Failed to send verification email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    currentUser.reload().then(() => {
      if (currentUser.emailVerified) {
        message.success('Email verified successfully!');
      } else {
        message.info('Email not yet verified. Please check your email and try again.');
      }
      setLoading(false);
    });
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (e) {
      message.error('Failed to sign out. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-form-container" style={{ flex: 1 }}>
          <div className="auth-form-wrapper" style={{ textAlign: 'center' }}>
            <Spin size="large" tip="Checking verification status..." />
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/auth/login" />;
  }

  if (!currentUser.emailVerified) {
    return (
      <div className="auth-container">
        <div className="auth-form-container" style={{ flex: 1 }}>
          <div className="auth-form-wrapper">
            <div className="auth-header" style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <MailOutlined style={{ fontSize: 48, color: '#006971' }} />
              </div>
              <h2>Email Verification Required</h2>
              <p>{isNewUser ? 'Please verify your email address to get started' : 'Please verify your email address to continue'}</p>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <Text style={{ display: 'block', marginBottom: 12, color: '#666' }}>We've sent a verification email to:</Text>
              <div style={{
                padding: '14px 16px',
                background: '#FAFCFA',
                borderRadius: 12,
                border: '2px solid #E8F5E8',
                marginBottom: 8
              }}>
                <Text strong style={{ fontSize: 16, color: '#333' }}>{currentUser.email}</Text>
              </div>
              <Paragraph style={{ color: '#8c8c8c', margin: 0, fontSize: 14 }}>
                Click the verification link in your email, then click "I've Verified" below.
              </Paragraph>
            </div>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Button
                type="primary"
                size="large"
                block
                icon={<CheckCircleOutlined />}
                onClick={handleRefresh}
                loading={loading}
                className="signin-button"
              >
                I've Verified My Email
              </Button>

              <Button
                size="large"
                block
                icon={<ReloadOutlined />}
                onClick={handleResendVerification}
                loading={sendingEmail}
                className="google-button"
              >
                Resend Verification Email
              </Button>
            </Space>

            <Divider className="auth-divider"><span>Need help?</span></Divider>

            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary">• Check your spam or junk folder</Text>
              <Text type="secondary">• Add our email to your contacts</Text>
              <Text type="secondary">• Verification links expire in 24 hours</Text>
            </Space>

            <div style={{ marginTop: 16 }}>
              <Button
                size="large"
                block
                danger
                icon={<LogoutOutlined />}
                onClick={handleSignOut}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default EmailVerificationMiddleware; 